import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, LogOut, User } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/use-auth";
import { SITE_OWNER_EMAIL } from "@shared/constants";

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [founderOpen, setFounderOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileFounderOpen, setMobileFounderOpen] = useState(false);
  const [mobileMediaOpen, setMobileMediaOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const isSiteOwner = (user?.email || "").toLowerCase() === SITE_OWNER_EMAIL;
  const [, navigate] = useLocation();
  const founderDropdownRef = useRef<HTMLDivElement>(null);
  const mediaDropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const founderBtnRef = useRef<HTMLButtonElement>(null);
  const mediaBtnRef = useRef<HTMLButtonElement>(null);
  const userMenuBtnRef = useRef<HTMLButtonElement>(null);
  const hamburgerBtnRef = useRef<HTMLButtonElement>(null);

  const closeAllDropdowns = useCallback(() => {
    setFounderOpen(false);
    setMediaOpen(false);
    setUserMenuOpen(false);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (founderDropdownRef.current && !founderDropdownRef.current.contains(e.target as Node)) {
        setFounderOpen(false);
      }
      if (mediaDropdownRef.current && !mediaDropdownRef.current.contains(e.target as Node)) {
        setMediaOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (founderOpen) {
          setFounderOpen(false);
          founderBtnRef.current?.focus();
        } else if (mediaOpen) {
          setMediaOpen(false);
          mediaBtnRef.current?.focus();
        } else if (userMenuOpen) {
          setUserMenuOpen(false);
          userMenuBtnRef.current?.focus();
        } else if (isMenuOpen) {
          setIsMenuOpen(false);
          hamburgerBtnRef.current?.focus();
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [founderOpen, mediaOpen, userMenuOpen, isMenuOpen, closeAllDropdowns]);

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => {
        setUserMenuOpen(false);
        setIsMenuOpen(false);
        navigate("/");
      },
    });
  };

  const navLinks = [
    { name: "Services", href: "/#services" },
    { name: "About", href: "/#about" },
    { name: "Community", href: isAuthenticated ? "/community" : "/login", isRoute: true },
  ];

  const mediaLinks = [
    { name: "Media & Ventures", href: "/#media" },
    { name: "Podcast", href: "/podcast" },
    { name: "Blog", href: "/blog" },
    { name: "Book Pre-Order", href: "/book" },
  ];

  const founderLinks = [
    { name: "Investor Directory", href: "/investor-directory" },
    { name: "Accelerator & Incubator Directory", href: "/accelerators" },
    { name: "Cloud Credit Program Directory", href: "/cloud-credits" },
    { name: "Company Registrations", href: "/company-registrations" },
    { name: "Trademark Registrations", href: "/trademark-registrations" },
    { name: "Patent Registrations", href: "/patent-registrations" },
    { name: "Startup Calculators", href: "/calculators" },
  ];

  return (
    <nav
      className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-white/5"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-3 group">
          <img
            src="/matt-rebeiro-logo.svg"
            alt="Matt Rebeiro Logo"
            width={45}
            height={56}
            fetchPriority="high"
            decoding="sync"
            className="h-8 w-auto group-hover:opacity-80 transition-opacity"
          />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <div className="flex gap-6 items-center">
            {navLinks.map((link) =>
              link.isRoute ? (
                <Link
                  key={link.name}
                  href={link.href}
                  className="text-sm font-medium text-white hover:text-primary transition-colors"
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-sm font-medium text-white hover:text-primary transition-colors"
                >
                  {link.name}
                </a>
              )
            )}

            <div className="relative" ref={mediaDropdownRef}>
              <button
                ref={mediaBtnRef}
                onClick={() => setMediaOpen(!mediaOpen)}
                className="text-sm font-medium text-white hover:text-primary transition-colors flex items-center gap-1 py-2 min-h-[44px]"
                data-testid="button-media-ventures"
                aria-expanded={mediaOpen}
                aria-haspopup="true"
              >
                Media & Ventures
                <ChevronDown size={14} className={`transition-transform ${mediaOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
              {mediaOpen && (
                <div
                  className="absolute top-full right-0 mt-2 w-56 bg-card border border-white/10 rounded-lg shadow-xl py-2 animate-in fade-in slide-in-from-top-2 duration-200"
                  role="list"
                  aria-label="Media & Ventures submenu"
                >
                  {mediaLinks.map((link) =>
                    link.href.startsWith("/") && !link.href.startsWith("/#") ? (
                      <Link
                        key={link.name}
                        href={link.href}
                        className="block px-4 py-2.5 text-sm text-white hover:text-primary hover:bg-white/5 transition-colors"
                        onClick={() => setMediaOpen(false)}
                        data-testid={`link-media-${link.href.slice(1)}`}
                        role="listitem"
                      >
                        {link.name}
                      </Link>
                    ) : (
                      <a
                        key={link.name}
                        href={link.href}
                        className="block px-4 py-2.5 text-sm text-white hover:text-primary hover:bg-white/5 transition-colors"
                        onClick={() => setMediaOpen(false)}
                        data-testid={`link-media-overview`}
                        role="listitem"
                      >
                        {link.name}
                      </a>
                    )
                  )}
                </div>
              )}
            </div>

            <div className="relative" ref={founderDropdownRef}>
              <button
                ref={founderBtnRef}
                onClick={() => setFounderOpen(!founderOpen)}
                className="text-sm font-medium text-white hover:text-primary transition-colors flex items-center gap-1 py-2 min-h-[44px]"
                data-testid="button-founder-resources"
                aria-expanded={founderOpen}
                aria-haspopup="true"
              >
                Founder Resources
                <ChevronDown size={14} className={`transition-transform ${founderOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
              {founderOpen && (
                <div
                  className="absolute top-full right-0 mt-2 w-64 bg-card border border-white/10 rounded-lg shadow-xl py-2 animate-in fade-in slide-in-from-top-2 duration-200"
                  role="list"
                  aria-label="Founder Resources submenu"
                >
                  {founderLinks.map((link) => (
                    <Link
                      key={link.name}
                      href={link.href}
                      className="block px-4 py-2.5 text-sm text-white hover:text-primary hover:bg-white/5 transition-colors"
                      onClick={() => setFounderOpen(false)}
                      data-testid={`link-founder-${link.href.slice(1)}`}
                      role="listitem"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {isAuthenticated && user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                ref={userMenuBtnRef}
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-white/10 hover:border-white/20 transition-colors min-h-[44px]"
                data-testid="button-user-menu"
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
                aria-label={`User menu for ${user.displayName || user.username}`}
              >
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center" aria-hidden="true">
                  <User size={14} className="text-primary" />
                </div>
                <span className="text-sm font-medium text-white max-w-[120px] truncate">
                  {user.displayName || user.username}
                </span>
                <ChevronDown size={14} className={`text-white/80 transition-transform ${userMenuOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
              {userMenuOpen && (
                <div
                  className="absolute top-full right-0 mt-2 w-56 bg-card border border-white/10 rounded-lg shadow-xl py-2 animate-in fade-in slide-in-from-top-2 duration-200"
                  role="list"
                  aria-label="User menu"
                >
                  <div className="px-4 py-2.5 border-b border-white/5">
                    <p className="text-sm font-medium text-white truncate">{user.displayName || user.username}</p>
                    <p className="text-xs text-white/70 truncate">{user.email || user.username}</p>
                    {user.isAdmin && (
                      <span className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                        Admin
                      </span>
                    )}
                  </div>
                  <Link
                    href="/profile"
                    className="block px-4 py-2.5 text-sm text-white hover:text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                    data-testid="link-profile-settings"
                    role="listitem"
                  >
                    Profile Settings
                  </Link>
                  <Link
                    href="/community"
                    className="block px-4 py-2.5 text-sm text-white hover:text-primary hover:bg-white/5 transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                    role="listitem"
                  >
                    Community Hub
                  </Link>
                  {user.isAdmin && (
                    <Link
                      href="/admin/users"
                      className="block px-4 py-2.5 text-sm text-white hover:text-primary hover:bg-white/5 transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                      data-testid="link-user-management"
                      role="listitem"
                    >
                      User Management
                    </Link>
                  )}
                  {isSiteOwner && (
                    <Link
                      href="/admin/users#newsletter-subs"
                      className="block px-4 py-2.5 text-sm text-white hover:text-primary hover:bg-white/5 transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                      data-testid="link-newsletter-subs"
                      role="listitem"
                    >
                      Newsletter Subs
                    </Link>
                  )}
                  {isSiteOwner && (
                    <Link
                      href="/admin/landing-pages"
                      className="block px-4 py-2.5 text-sm text-white hover:text-primary hover:bg-white/5 transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                      data-testid="link-landing-pages"
                      role="listitem"
                    >
                      Landing Pages
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-white/5 transition-colors flex items-center gap-2"
                    data-testid="button-logout"
                    role="listitem"
                  >
                    <LogOut size={14} aria-hidden="true" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Button className="font-semibold" asChild>
              <a href="mailto:advisor@mattrebeiro.com">Book a Call</a>
            </Button>
          )}
        </div>

        <button
          ref={hamburgerBtnRef}
          className="md:hidden p-3 text-white min-w-[44px] min-h-[44px] flex items-center justify-center"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
        </button>
      </div>

      {isMenuOpen && (
        <div className="md:hidden absolute top-20 left-0 w-full bg-background border-b border-white/5 p-6 flex flex-col gap-4 animate-in slide-in-from-top-2 max-h-[calc(100vh-5rem)] overflow-y-auto" role="list" aria-label="Mobile navigation menu">
          {isAuthenticated && user && (
            <Link
              href="/profile"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-3 pb-4 mb-2 border-b border-white/10 hover:opacity-80 transition-opacity"
              data-testid="link-mobile-profile"
              role="listitem"
            >
              <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0" aria-hidden="true">
                <User size={16} className="text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white truncate" data-testid="text-mobile-username">
                  {user.displayName || user.username}
                </p>
                <p className="text-xs text-white/70 truncate">{user.email || user.username}</p>
                {user.isAdmin && (
                  <span className="inline-block mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                    Admin
                  </span>
                )}
              </div>
              <ChevronDown size={14} className="text-white/80 -rotate-90 shrink-0" aria-hidden="true" />
            </Link>
          )}

          {navLinks.map((link) =>
            link.isRoute ? (
              <Link
                key={link.name}
                href={link.href}
                className="text-lg font-medium text-white py-2 border-b border-white/5"
                onClick={() => setIsMenuOpen(false)}
                role="listitem"
              >
                {link.name}
              </Link>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className="text-lg font-medium text-white py-2 border-b border-white/5"
                onClick={() => setIsMenuOpen(false)}
                role="listitem"
              >
                {link.name}
              </a>
            )
          )}

          <button
            className="text-lg font-medium text-white py-2 border-b border-white/5 text-left flex items-center justify-between"
            onClick={() => setMobileMediaOpen(!mobileMediaOpen)}
            aria-expanded={mobileMediaOpen}
            aria-haspopup="true"
          >
            Media & Ventures
            <ChevronDown size={18} className={`transition-transform ${mobileMediaOpen ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
          {mobileMediaOpen && (
            <div className="pl-4 flex flex-col gap-2" role="list" aria-label="Media & Ventures submenu">
              {mediaLinks.map((link) =>
                link.href.startsWith("/") && !link.href.startsWith("/#") ? (
                  <Link
                    key={link.name}
                    href={link.href}
                    className="text-base text-white hover:text-primary py-1.5"
                    onClick={() => setIsMenuOpen(false)}
                    role="listitem"
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="text-base text-white hover:text-primary py-1.5"
                    onClick={() => setIsMenuOpen(false)}
                    role="listitem"
                  >
                    {link.name}
                  </a>
                )
              )}
            </div>
          )}

          <button
            className="text-lg font-medium text-white py-2 border-b border-white/5 text-left flex items-center justify-between"
            onClick={() => setMobileFounderOpen(!mobileFounderOpen)}
            aria-expanded={mobileFounderOpen}
            aria-haspopup="true"
          >
            Founder Resources
            <ChevronDown size={18} className={`transition-transform ${mobileFounderOpen ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
          {mobileFounderOpen && (
            <div className="pl-4 flex flex-col gap-2" role="list" aria-label="Founder Resources submenu">
              {founderLinks.map((link) => (
                <Link
                  key={link.name}
                  href={link.href}
                  className="text-base text-white hover:text-primary py-1.5"
                  onClick={() => setIsMenuOpen(false)}
                  role="listitem"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          )}

          {isAuthenticated && user?.isAdmin && (
            <Link
              href="/admin/users"
              className="text-lg font-medium text-primary py-2 border-b border-white/5"
              onClick={() => setIsMenuOpen(false)}
              data-testid="link-mobile-user-management"
              role="listitem"
            >
              User Management
            </Link>
          )}
          {isAuthenticated && isSiteOwner && (
            <Link
              href="/admin/users#newsletter-subs"
              className="text-lg font-medium text-primary py-2 border-b border-white/5"
              onClick={() => setIsMenuOpen(false)}
              data-testid="link-mobile-newsletter-subs"
              role="listitem"
            >
              Newsletter Subs
            </Link>
          )}
          {isAuthenticated && isSiteOwner && (
            <Link
              href="/admin/landing-pages"
              className="text-lg font-medium text-primary py-2 border-b border-white/5"
              onClick={() => setIsMenuOpen(false)}
              data-testid="link-mobile-landing-pages"
              role="listitem"
            >
              Landing Pages
            </Link>
          )}

          {isAuthenticated ? (
            <button
              onClick={handleLogout}
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors font-medium"
              data-testid="button-mobile-logout"
              role="listitem"
            >
              <LogOut size={16} aria-hidden="true" />
              Sign Out
            </button>
          ) : (
            <Button className="mt-4 w-full" asChild>
              <a href="mailto:advisor@mattrebeiro.com" role="listitem">Book a Call</a>
            </Button>
          )}
        </div>
      )}
    </nav>
  );
}
